/**
 * PlayerSportStats
 *
 * Sport-specific performance statistics page for the current user.
 * Renders a sport card (e.g. VolleyballStatsCard) based on the selected sport.
 * When multiple sports are supplied via the `sports` prop, a tab strip is shown
 * so the user can switch between them without leaving the overlay.
 *
 * Props:
 *   sport         — { id, name, skillLevel } — initially selected sport
 *   sports        — array of all user sports (optional, enables tabs)
 *   onSportChange — (sport) => void  — called when tab selection changes
 *   onBack        — () => void
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import teamService from '../../../services/api/teamService';
import VolleyballStatsCard  from './VolleyballStatsCard';
import BasketballStatsCard  from './BasketballStatsCard';
import FootballStatsCard    from './FootballStatsCard';
import BadmintonStatsCard   from './BadmintonStatsCard';
import TableTennisStatsCard from './TableTennisStatsCard';
import { toSlug } from '../../../utils/sportSlug';
import '../../../styles/components/sportStats.css';

// Map sport name → component
const SPORT_CARD_MAP = {
  volleyball:        VolleyballStatsCard,
  basketball:        BasketballStatsCard,
  football:          FootballStatsCard,
  badminton:         BadmintonStatsCard,
  badminton_doubles: BadmintonStatsCard,
  table_tennis:      TableTennisStatsCard,
};

const LoadingPlaceholder = () => (
  <div className="pss-loading">
    <div className="pss-spinner" />
    <p>Loading your stats…</p>
  </div>
);

const ErrorPlaceholder = ({ message, onRetry }) => (
  <div className="pss-error">
    <span>⚠️</span>
    <p>{message}</p>
    {onRetry && <button className="pss-retry-btn" onClick={onRetry}>Retry</button>}
  </div>
);

const UnsupportedSport = ({ sportName }) => (
  <div className="pss-unsupported">
    <div className="pss-unsupported-icon">📊</div>
    <h4>Advanced stats for {sportName} coming soon</h4>
    <p>Play live matches in Advanced Scoring mode to start tracking player metrics for this sport.</p>
  </div>
);

const PlayerSportStats = ({ sport, sports = [], onSportChange, onBack }) => {
  const { user } = useAuth();
  // activeSport is locally controlled; parent can override via `sport` prop
  const [activeSport, setActiveSport] = useState(sport);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Sync if parent changes the `sport` prop
  useEffect(() => {
    if (sport?.id && sport.id !== activeSport?.id) {
      setActiveSport(sport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sport?.id]);

  // Same slug as the /stats/:sportSlug route, so "Table Tennis" → table_tennis (ST-B04)
  const sportKey = toSlug(activeSport?.name);
  const StatsCard = SPORT_CARD_MAP[sportKey] || null;

  const fetchStats = async () => {
    if (!user?.id || !activeSport?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await teamService.getPlayerCareerStats(user.id, activeSport.id);
      setStats(data);
    } catch (err) {
      setError(err?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSport?.id) fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSport?.id, user?.id]);

  const handleTabClick = (s) => {
    setActiveSport(s);
    setStats(null);
    if (onSportChange) onSportChange(s);
  };

  // Deduplicate sports list (always include activeSport)
  const tabSports = sports.length > 0 ? sports : (activeSport ? [activeSport] : []);

  return (
    <div className="pss-page">
      <div className="pss-topbar">
        <button className="pss-back-btn" onClick={onBack}>
          ← Back
        </button>
        <div className="pss-topbar-title">
          My Stats
          {activeSport?.skillLevel && <span className="pss-skill-badge">{activeSport.skillLevel}</span>}
        </div>
      </div>

      {/* Sport tab strip — only shown when there are multiple sports */}
      {tabSports.length > 1 && (
        <div className="pss-tabs">
          {tabSports.map((s) => (
            <button
              key={s.id}
              className={`pss-tab${activeSport?.id === s.id ? ' pss-tab--active' : ''}`}
              onClick={() => handleTabClick(s)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Single-sport label when no tabs */}
      {tabSports.length === 1 && (
        <div className="pss-single-sport-label">{activeSport?.name}</div>
      )}

      {loading && <LoadingPlaceholder />}
      {error   && <ErrorPlaceholder message={error} onRetry={fetchStats} />}

      {!loading && !error && (
        StatsCard
          ? <StatsCard
              stats={stats || {}}
              user={user}
              sportProfile={{
                position:     activeSport?.position     || null,
                playingStyle: activeSport?.playingStyle || null,
              }}
            />
          : <UnsupportedSport sportName={activeSport?.name} />
      )}
    </div>
  );
};

export default PlayerSportStats;
